import React from 'react';
import axios from 'axios';
import { NodeProps, useReactFlow } from 'reactflow';
import { useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck, faClipboard } from '@fortawesome/free-solid-svg-icons';
import CopyToClipboard from 'react-copy-to-clipboard';
import { IoCheckmarkCircleOutline, IoWarningOutline } from 'react-icons/io5';
import { operationType } from '../../../../../services/types';
import { updateNodeValue } from '../../../../../services/method';
import { webURL } from '../../../../../services/API';
import { RootState } from '../../../../../redux/store';
import { ExpandedBlock } from '../../../expanded';
import { SettingsBlock, settingsDataType } from '../../../../settingsBlock';
import { AuthenticateSelect } from '../../../../auth/select';
import { Loader, Main, Styled } from './style';

type ShopifyTriggerProps = NodeProps<operationType> & {
  setChildMinimize?: (fn: (id: string) => void) => void;
  setChildSettings?: (fn: (id: string) => void) => void;
};

const DEFAULT_TOPIC = 'orders/create';

const topicOptions: { label: string; value: string }[] = [
  { label: 'Orders: Created', value: 'orders/create' },
  { label: 'Orders: Paid', value: 'orders/paid' },
  { label: 'Orders: Fulfilled', value: 'orders/fulfilled' },
  { label: 'Customers: Created', value: 'customers/create' },
  { label: 'Customers: Updated', value: 'customers/update' },
  { label: 'Products: Created', value: 'products/create' },
  { label: 'Products: Updated', value: 'products/update' },
  { label: 'Products: Deleted', value: 'products/delete' },
  { label: 'Checkouts: Created', value: 'checkouts/create' },
  { label: 'Checkouts: Updated', value: 'checkouts/update' },
];

const supportedTopics = new Set(topicOptions.map(option => option.value));

function normalizeTopics(eventTopics: unknown, eventTopic?: string): string[] {
  const fromArray = Array.isArray(eventTopics)
    ? eventTopics
      .filter((item): item is string => typeof item === 'string')
      .map(item => item.trim())
      .filter(item => item.length > 0 && supportedTopics.has(item))
    : [];

  if (fromArray.length > 0) {
    return Array.from(new Set(fromArray));
  }

  const single = typeof eventTopic === 'string' ? eventTopic.trim() : '';
  if (single && supportedTopics.has(single)) {
    return [single];
  }

  return [];
}

export function ShopifyTrigger({ id, data, setChildMinimize, setChildSettings }: ShopifyTriggerProps) {
  const { setNodes } = useReactFlow();
  const [searchParams] = useSearchParams();
  const userToken = useSelector((state: RootState) => state.user.data?.token);
  const projectId = searchParams.get('id');

  const [settings, setSettings] = React.useState<settingsDataType>({ isToggle: false });
  const [isExpanded, setIsExpanded] = React.useState(false);
  const [isCopy, setIsCopy] = React.useState<boolean>(false);
  const [isSecretCopy, setIsSecretCopy] = React.useState<boolean>(false);
  const [link, setLink] = React.useState<string>('');
  const [isLoading, setIsLoading] = React.useState<boolean>(false);
  const [error, setError] = React.useState<string>('');
  const [isTopicSectionOpen, setIsTopicSectionOpen] = React.useState<boolean>(true);
  const [isWebhookUrlSectionOpen, setIsWebhookUrlSectionOpen] = React.useState<boolean>(false);

  const selectedTopics = React.useMemo(
    () => normalizeTopics(data.value?.eventTopics, data.value?.eventTopic),
    [data.value?.eventTopics, data.value?.eventTopic]
  );

  React.useEffect(() => {
    if (setChildMinimize) setChildMinimize(() => { setIsExpanded(prev => !prev); });
    if (setChildSettings) setChildSettings(() => { setSettings(prev => ({ ...prev, isToggle: !prev.isToggle })); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [setChildMinimize, setChildSettings]);

  React.useEffect(() => {
    const nextTopics = normalizeTopics(data.value?.eventTopics, data.value?.eventTopic);
    if (nextTopics.length === 0) {
      updateNodeValue(id, setNodes, { eventTopics: [DEFAULT_TOPIC], eventTopic: DEFAULT_TOPIC });
      return;
    }

    if (!Array.isArray(data.value?.eventTopics)) {
      updateNodeValue(id, setNodes, { eventTopics: nextTopics, eventTopic: nextTopics[0] });
      return;
    }

    const currentTopics = (data.value.eventTopics as unknown[])
      .filter((item: unknown): item is string => typeof item === 'string')
      .map((item: string) => item.trim());
    const isSame =
      currentTopics.length === nextTopics.length &&
      currentTopics.every((item: string, index: number) => item === nextTopics[index]);

    if (!isSame || data.value?.eventTopic !== nextTopics[0]) {
      updateNodeValue(id, setNodes, { eventTopics: nextTopics, eventTopic: nextTopics[0] });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data.value?.eventTopics, data.value?.eventTopic]);

  React.useEffect(() => {
    if (data?.triggerCredentials?.webhookUrl) {
      setLink(data.triggerCredentials.webhookUrl);
      setError('');
    }
  }, [data?.triggerCredentials?.webhookUrl]);

  React.useEffect(() => {
    if (!isExpanded || link || !data.value?.connection || selectedTopics.length === 0) return;

    setIsLoading(true);
    const timeout = setTimeout(() => {
      void generateLink();
    }, 6000);

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isExpanded, selectedTopics.join('|')]);

  const handleAuthenticate = async (type: 'add' | 'remove', connectionData: any) => {
    if (type === 'add') {
      updateNodeValue(id, setNodes, { connection: connectionData });
    } else {
      updateNodeValue(id, setNodes, { connection: null });
    }
  };

  const handleToggleTopic = (topic: string) => {
    if (!supportedTopics.has(topic)) return;
    const currentTopics = normalizeTopics(data.value?.eventTopics, data.value?.eventTopic);
    const nextTopics = currentTopics.includes(topic)
      ? currentTopics.filter(item => item !== topic)
      : [...currentTopics, topic];

    updateNodeValue(id, setNodes, {
      eventTopics: nextTopics,
      eventTopic: nextTopics[0] || '',
    });
    setLink('');
  };

  const handleWebhookSecretChange = (value: string) => {
    updateNodeValue(id, setNodes, { webhookClientSecret: value.trim() });
  };

  const handleToggleTopicsSection = () => {
    setIsTopicSectionOpen(prev => {
      const next = !prev;
      if (next) {
        setIsWebhookUrlSectionOpen(false);
      }
      return next;
    });
  };

  const handleToggleWebhookUrlSection = () => {
    setIsWebhookUrlSectionOpen(prev => {
      const next = !prev;
      if (next) {
        setIsTopicSectionOpen(false);
      }
      return next;
    });
  };

  const generateLink = async () => {
    try {
      if (!projectId) throw new Error('Missing project id');
      if (!userToken) throw new Error('Missing auth token');
      if (!data.value?.connection?.id) throw new Error('Please select a Shopify connection first');

      const eventTopics = normalizeTopics(data.value?.eventTopics, data.value?.eventTopic);
      if (eventTopics.length === 0) throw new Error('Please select at least one event topic');

      const eventTopic = eventTopics[0];
      const webhookClientSecret = data.value?.webhookClientSecret?.trim() || '';

      const response = await axios.post(
        `${webURL}/triggers/shopify/generate-link`,
        {
          nodeIndex: data.index,
          projectId,
          eventTopic,
          eventTopics,
          connectionId: data.value?.connection?.id,
          webhookClientSecret,
        },
        {
          headers: {
            'x-auth-token': userToken,
          },
        }
      );

      const { nodeId, webhookUrl, blockId } = response.data;

      setNodes(prev =>
        prev.map(node =>
          node.data.index === data.index
            ? {
                ...node,
                data: {
                  ...node.data,
                  value: {
                    ...node.data.value,
                    eventTopics,
                    eventTopic,
                    webhookClientSecret,
                  },
                  triggerCredentials: { nodeId, webhookUrl, blockId },
                },
              }
            : node
        )
      );

      setLink(webhookUrl);
      setError('');
    } catch (err: any) {
      console.error('Error generating Shopify webhook URL:', err);
      const message = err.response?.data?.message || err.message || 'Failed to generate webhook URL';
      setError(message.includes('Trigger node does not exist')
        ? 'Please SAVE the project first (Ctrl+S), then reopen this panel.'
        : message
      );
    } finally {
      setIsLoading(false);
    }
  };

  const isWarning = !data.value?.connection || selectedTopics.length === 0;
  const hasSecret = !!data.value?.webhookClientSecret?.trim();

  return (
    <>
      <SettingsBlock nodeId={id} data={settings} setData={setSettings} />
      <Main>
        {isWarning ? (
          <>
            <div id="warning"></div>
            <span id="warningIcon"><IoWarningOutline size={21} /></span>
          </>
        ) : data.output ? (
          <>
            <div id="ran"></div>
            <span id="ranIcon"><IoCheckmarkCircleOutline size={21} /></span>
          </>
        ) : null}

        {isExpanded ? (
          <ExpandedBlock
            data={{ id, ...data }}
            handleClose={() => setIsExpanded(false)}
            isWarning={isWarning}
            style={{ width: '480px' }}
          >
            {isLoading ? <Loader /> : (
              <Styled>
                <div className="action">
                  <AuthenticateSelect
                    type="shopify"
                    defaultId={data.value?.connection?.id}
                    handleAuthenticate={handleAuthenticate}
                    background={data.background}
                  />
                </div>

                <div className="topicSelect sectionCard">
                  <button
                    type="button"
                    className="sectionToggle"
                    onClick={handleToggleTopicsSection}
                  >
                    <span>Event Topics</span>
                    <span className="sectionToggleIcon">{isTopicSectionOpen ? '-' : '+'}</span>
                  </button>

                  {isTopicSectionOpen ? (
                    <div className="topicChecklist">
                      {topicOptions.map(option => {
                        const checked = selectedTopics.includes(option.value);
                        return (
                          <label key={option.value} className="topicItem">
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => handleToggleTopic(option.value)}
                            />
                            <span>{option.label}</span>
                          </label>
                        );
                      })}
                    </div>
                  ) : null}
                </div>

                <div className="toolbar">
                  <button
                    className="generateBtn"
                    onClick={() => {
                      setIsLoading(true);
                      void generateLink();
                    }}
                    disabled={!data.value?.connection || selectedTopics.length === 0}
                  >
                    Generate Link
                  </button>
                </div>

                {isWarning ? <div className="warning">Select Shopify connection and at least one event topic to generate URL.</div> : null}
                {error ? <div className="error">{error}</div> : null}

                <div className="webhook-section sectionCard">
                  <button
                    type="button"
                    className="sectionToggle"
                    onClick={handleToggleWebhookUrlSection}
                  >
                    <span>Shopify Webhook URL + Secret</span>
                    <span className="sectionToggleIcon">{isWebhookUrlSectionOpen ? '-' : '+'}</span>
                  </button>

                  {isWebhookUrlSectionOpen ? (
                    <div className="webhookPanelBody">
                      <div className="webhookGroup">
                        <div className="groupTitle">Shopify Webhook URL</div>
                        {link ? (
                          <CopyToClipboard
                            text={link}
                            onCopy={() => {
                              setIsCopy(true);
                              setTimeout(() => setIsCopy(false), 500);
                            }}
                          >
                            <div className="link-container" title={link}>
                              <div className="link-with-copy">
                                <FontAwesomeIcon
                                  icon={isCopy ? faCheck : faClipboard}
                                  className={`copy-icon ${isCopy ? 'copied' : ''}`}
                                />
                                <span className="link-text">{link}</span>
                              </div>
                            </div>
                          </CopyToClipboard>
                        ) : (
                          <div className="warning">Generate webhook URL first.</div>
                        )}
                      </div>

                      <div className="webhookGroup">
                        <div className="groupTitle">Webhook Signing Secret</div>
                        <div className="secret-hint">
                          Paste the value Shopify shows in this format:
                          {' '}
                          <strong>Your webhooks will be signed with ...</strong>
                        </div>
                        <input
                          className="secret-input"
                          type="text"
                          value={data.value?.webhookClientSecret || ''}
                          placeholder="12600c790d7f0d9e0f17e324c56604c857124cad3c3b48fae6171073519d5c40"
                          onChange={(event) => handleWebhookSecretChange(event.target.value)}
                          autoComplete="off"
                          spellCheck={false}
                        />

                        {data.value?.webhookClientSecret ? (
                          <CopyToClipboard
                            text={data.value.webhookClientSecret}
                            onCopy={() => {
                              setIsSecretCopy(true);
                              setTimeout(() => setIsSecretCopy(false), 500);
                            }}
                          >
                            <div className="link-container" title={data.value.webhookClientSecret}>
                              <div className="link-with-copy">
                                <FontAwesomeIcon
                                  icon={isSecretCopy ? faCheck : faClipboard}
                                  className={`copy-icon ${isSecretCopy ? 'copied' : ''}`}
                                />
                                <span className="link-text">{data.value.webhookClientSecret}</span>
                              </div>
                            </div>
                          </CopyToClipboard>
                        ) : (
                          <div className="warning">Paste signing secret to verify Shopify HMAC.</div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div className="collapsedValue">
                      <span className={link ? 'statusBadge ready' : 'statusBadge missing'}>
                        URL: {link ? 'Configured' : 'Missing'}
                      </span>
                      <span className={hasSecret ? 'statusBadge ready' : 'statusBadge missing'}>
                        Secret: {hasSecret ? 'Configured' : 'Missing'}
                      </span>
                    </div>
                  )}
                </div>

                <div className="instructions-section">
                  <h5>Setup Instructions</h5>
                  <ol className="steps-list">
                    <li>Open Shopify Admin for your store.</li>
                    <li>Navigate to webhook settings for your app/store.</li>
                    <li>Select topic(s): <strong>{selectedTopics.length ? selectedTopics.join(', ') : DEFAULT_TOPIC}</strong>.</li>
                    <li>Paste the generated URL and save.</li>
                    <li>Trigger a real Shopify action to test end-to-end.</li>
                  </ol>
                </div>
              </Styled>
            )}
          </ExpandedBlock>
        ) : null}
      </Main>
    </>
  );
}